import React, { memo } from 'react';
import styled, { css } from 'styled-components';
import { NodeProps, Position } from 'reactflow';
import { themeColor } from '../../theme/colors';
import { xsRegular, xxsLight } from '../../theme/typography';
import { CustomHandle, HandleRightInvisible } from './CustomHandle';
import { NodeData } from './types';
import {
  ellipsisMixin,
  rainboxTextMixin,
  transitionFunc,
} from '../../theme/utils';
import { TextTooltip } from '../../TextTooltip/TextTooltip';

export const FLOWCHART_MIN_NODE_COMPACT = 32;
export const FLOWCHART_MIN_NODE_HEIGHT = 45;

export const CenterFunctionNode = memo(({ data }: NodeProps<NodeData>) => {
  return (
    <Container
      $highlight={data.highlight}
      $compact={!!data.compact}
      $width={data.width}
      $clickable={!data.notClickable}
    >
      <CustomHandle
        type="target"
        position={Position.Left}
        isConnectable={false}
        style={{ height: '30%' }}
      />
      {/* <HudIcon icon="other.codeBrackets" /> */}
      {data.title && (
        <Title colorful compact={!!data.compact}>
          {data.title}
        </Title>
      )}
      <Label>{data.label}</Label>

      <HandleRightInvisible
        type="source"
        position={Position.Right}
        isConnectable={false}
      />
    </Container>
  );
});

export const InputFunctionNode = memo(
  ({ data, ...rest }: NodeProps<NodeData>) => {
    return (
      <TextTooltip label={data.tooltip || ''}>
        <Container
          {...rest}
          $highlight={data.highlight}
          $cta={!!data.cta?.length}
          $compact={!!data.compact}
          $width={data.width}
          $tintColor={data.tintColor}
          $clickable={!data.notClickable}
        >
          <HandleRightInvisible
            type="source"
            position={Position.Right}
            isConnectable={false}
          />
          {/* <HudIcon icon="other.codeBrackets" /> */}
          {data.title && <Title compact={!!data.compact}>{data.title}</Title>}
          <Label>{data.label}</Label>
        </Container>
      </TextTooltip>
    );
  },
);

export const OutputFunctionNode = memo(({ data }: NodeProps<NodeData>) => {
  const height = data.handles ? `${Math.max(data.handles[0], 5)}px` : '30%';

  return (
    <TextTooltip label={data.tooltip || ''} offset={-10}>
      <Container
        $highlight={data.highlight}
        $cta={!!data.cta?.length}
        $compact={!!data.compact}
        $width={data.width}
        $tintColor={data.tintColor}
        $tintDirection="left"
        $clickable={!data.notClickable}
      >
        <CustomHandle
          type="target"
          position={Position.Left}
          onConnect={(params) => console.log('handle onConnect', params)}
          isConnectable={false}
          style={{ height }}
          $hidden={!!data.cta}
        />
        {/* <HudIcon icon="other.codeBrackets" /> */}
        {data.title && <Title compact={!!data.compact}>{data.title}</Title>}
        <Label>{data.label}</Label>
      </Container>
    </TextTooltip>
  );
});

const Title = styled.span<{ colorful?: boolean; compact?: boolean }>`
  font-weight: ${({ compact }) => (compact ? '500' : '600')};
  /* white-space: nowrap; */
  ${({ colorful }) => colorful && rainboxTextMixin}
`;

const Label = styled.span`
  opacity: 0.7;
  /* background: linear-gradient(90deg, #ad86ed 0%, #25fce7 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent; */
`;

const dynamicTurquoise = {
  dark: 'turquoise',
  light: 'turquoiseTertiary',
};

const Container = styled.div<{
  $highlight?: boolean;
  $cta?: boolean;
  $compact?: boolean;
  $width?: string;
  $tintColor?: string;
  $tintDirection?: 'left' | 'right';
  $clickable?: boolean;
}>`
  ${xsRegular}
  padding: 12px 16px;
  color: ${themeColor('textPrimary')};

  /* placeholder transparent outline */
  outline: 1px #00000000 solid;

  border-radius: 3px;
  &:hover {
    outline: 1px ${themeColor(dynamicTurquoise)} solid;
  }

  /* Highlight is either true/false or undefined */

  transition: opacity 1s ${transitionFunc}, outline 0.8s ${transitionFunc};
  ${(props) =>
    props.$highlight === true &&
    css`
      transition: opacity 0.2s ${transitionFunc};
      outline: 1px ${themeColor(dynamicTurquoise)} solid;
    `}

  ${(props) =>
    props.$highlight === false &&
    css`
      opacity: 0.2;
      transition: opacity 0.2s ${transitionFunc};
    `}

  box-sizing: border-box;

  background: ${themeColor('nodeBackground')};

  width: ${({ $width }) => $width ?? '250px'};
  min-height: ${FLOWCHART_MIN_NODE_HEIGHT}px;
  height: fit-content;

  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  ${(props) =>
    props.$cta &&
    css`
      align-items: center;
      ${props.$compact &&
      css`
        text-align: center;
      `}
    `}

  & > svg {
    /* red if $highlight */
    color: ${({ $highlight }) =>
      $highlight ? themeColor(dynamicTurquoise) : themeColor('textTertiary')};
  }

  & > span {
    // Break letters if they are too long
    word-break: break-all;
  }

  .react-flow__handle {
    background: 'orange';
    width: 8px;
    height: 10px;
    border-radius: 3px;
  }

  ${(props) =>
    props.$compact &&
    css`
      display: block;
      ${ellipsisMixin}
      ${xxsLight}
      padding: 8px 12px;
      min-height: ${FLOWCHART_MIN_NODE_COMPACT}px;

      & ${CustomHandle} {
        border-radius: 1px;
        left: 1px;
      }

      /* Hide label (e.g. params) on compact */
      & ${Label} {
        display: none;
      }
    `}

  ${(props) =>
    props.$tintColor &&
    css`
      background: linear-gradient(
          to ${props.$tintDirection ?? 'right'},
          ${themeColor('nodeBackground')} 70%,
          ${props.$tintColor} 170%
        ),
        ${themeColor('nodeBackground')};
    `}
`;
